import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getSpoilerPreferences,
  resolveAccessToken,
  updateSpoilerPreferences,
  type SpoilerPreferencesResponse,
  type UpdateSpoilerPreferencesInput,
} from '@nomiss/services';

export const spoilerShieldQueryKeys = {
  preferences: ['account', 'spoiler-preferences'] as const,
};

export function applySpoilerPreferencesPatch(
  current: SpoilerPreferencesResponse,
  input: UpdateSpoilerPreferencesInput,
): SpoilerPreferencesResponse {
  const sportsById = new Map(
    (input.sports ?? []).map(sport => [sport.sportId, sport.isEnabled]),
  );
  const teamsById = new Map(
    (input.teams ?? []).map(team => [team.teamId, team.isEnabled]),
  );

  const knownSportIds = new Set(current.sports.map(sport => sport.sportId));
  const appendedSports = (input.sports ?? [])
    .filter(sport => !knownSportIds.has(sport.sportId))
    .map(sport => ({
      sportId: sport.sportId,
      sportCode: '',
      sportName: '',
      isEnabled: sport.isEnabled,
    }));

  return {
    ...current,
    globalEnabled:
      input.global !== undefined ? input.global.isEnabled : current.globalEnabled,
    sports: [
      ...current.sports.map(sport =>
        sportsById.has(sport.sportId)
          ? { ...sport, isEnabled: sportsById.get(sport.sportId) === true }
          : sport,
      ),
      ...appendedSports,
    ],
    teams: current.teams.map(group => ({
      ...group,
      items: group.items.map(item =>
        teamsById.has(item.teamId)
          ? { ...item, isEnabled: teamsById.get(item.teamId) === true }
          : item,
      ),
    })),
  };
}

export function useSpoilerPreferences(
  accessToken?: string | null,
  enabled = true,
) {
  const token = accessToken ?? resolveAccessToken();

  return useQuery({
    queryKey: [...spoilerShieldQueryKeys.preferences, token ?? ''] as const,
    queryFn: () => getSpoilerPreferences(),
    enabled: enabled && Boolean(token),
  });
}

export function useUpdateSpoilerPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateSpoilerPreferencesInput) =>
      updateSpoilerPreferences(input),
    onMutate: async input => {
      await queryClient.cancelQueries({
        queryKey: spoilerShieldQueryKeys.preferences,
      });
      const snapshots = queryClient.getQueriesData<SpoilerPreferencesResponse>({
        queryKey: spoilerShieldQueryKeys.preferences,
      });
      snapshots.forEach(([key, current]) => {
        if (current) {
          queryClient.setQueryData(
            key,
            applySpoilerPreferencesPatch(current, input),
          );
        }
      });
      return { snapshots };
    },
    onError: (_error, _input, context) => {
      context?.snapshots.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
    onSuccess: data => {
      queryClient.setQueriesData(
        { queryKey: spoilerShieldQueryKeys.preferences },
        data,
      );
    },
  });
}
